/**
 * Geodata — CDN-backed country/region data.
 *
 * Instead of bundling the 775KB iso3166-2-db package, we fetch slim JSON
 * from Vercel Blob CDN on demand:
 *   - countries.json (~10KB) — fetched once on first getCountries() call
 *   - regions.json   (~95KB) — fetched lazily on first getStatesForCountry() call
 *
 * Both are cached in memory after first fetch. The API surface is identical
 * to the previous static-import version so all consumers work unchanged.
 */
// CDN base serving the pre-built slim geodata (uploaded by scripts/build-geodata-cdn.ts)
const GEODATA_CDN_BASE = 'https://cvwnizdbugpz6jwk.public.blob.vercel-storage.com/geodata/v1';
export const AVAILABLE_LANGUAGES = ['en', 'ru', 'de', 'da', 'fr', 'es', 'zh', 'hi', 'pt', 'ja', 'ar', 'it', 'he'];
// ─── In-memory cache ─────────────────────────────────────────────────────────
const countriesCache = new Map();
const regionsCache = new Map();
const inflightCountries = new Map();
const inflightRegions = new Map();
// ─── CDN fetchers (deduplicated, cached) ─────────────────────────────────────
async function fetchCountriesRaw(language) {
    const cached = countriesCache.get(language);
    if (cached)
        return cached;
    // Deduplicate concurrent fetches for the same language
    let inflight = inflightCountries.get(language);
    if (inflight)
        return inflight;
    inflight = (async () => {
        const url = `${GEODATA_CDN_BASE}/${language}/countries.json`;
        const res = await fetch(url);
        if (!res.ok)
            throw new Error(`Geodata fetch failed: ${url} (${res.status})`);
        const data = await res.json();
        countriesCache.set(language, data);
        inflightCountries.delete(language);
        return data;
    })();
    inflightCountries.set(language, inflight);
    return inflight;
}
async function fetchRegionsRaw(language) {
    const cached = regionsCache.get(language);
    if (cached)
        return cached;
    let inflight = inflightRegions.get(language);
    if (inflight)
        return inflight;
    inflight = (async () => {
        const url = `${GEODATA_CDN_BASE}/${language}/regions.json`;
        const res = await fetch(url);
        if (!res.ok)
            throw new Error(`Geodata fetch failed: ${url} (${res.status})`);
        const data = await res.json();
        regionsCache.set(language, data);
        inflightRegions.delete(language);
        return data;
    })();
    inflightRegions.set(language, inflight);
    return inflight;
}
// ─── Synchronous accessors (return cached data, empty if not yet loaded) ─────
// These preserve the existing synchronous API. Consumers should call
// ensureCountriesLoaded / ensureRegionsLoaded first (the useISOData hook does).
function getCountriesCached(language) {
    return countriesCache.get(language) || countriesCache.get('en') || {};
}
function getRegionsCached(language) {
    return regionsCache.get(language) || regionsCache.get('en') || {};
}
// ─── Public async loaders (call these before using sync getters) ─────────────
export async function ensureCountriesLoaded(language = 'en') {
    await fetchCountriesRaw(language);
}
export async function ensureRegionsLoaded(language = 'en') {
    await fetchRegionsRaw(language);
}
export async function ensureGeoDataLoaded(language = 'en') {
    await Promise.all([fetchCountriesRaw(language), fetchRegionsRaw(language)]);
}
// ─── Backward-compatible sync API ────────────────────────────────────────────
// These match the old iso3166.ts signatures exactly.
export const getCountries = (language = 'en') => {
    const raw = getCountriesCached(language);
    return Object.entries(raw)
        .map(([code, c]) => ({
        code,
        name: c.n,
        iso3: c.i3,
        numeric: c.nu,
        uniqueKey: `country-${code}`,
    }))
        .sort((a, b) => a.name.localeCompare(b.name));
};
export const getStatesForCountry = (countryCode, language = 'en') => {
    const raw = getRegionsCached(language);
    const tuples = raw[countryCode];
    if (!tuples)
        return [];
    return tuples
        .map(([iso, name], i) => ({
        code: iso,
        name,
        countryCode,
        uniqueKey: `state-${countryCode}-${iso}-${i}`,
    }))
        .sort((a, b) => a.name.localeCompare(b.name));
};
export const getAllStates = (language = 'en') => {
    const raw = getRegionsCached(language);
    const states = [];
    for (const [countryCode, tuples] of Object.entries(raw)) {
        tuples.forEach(([iso, name], i) => {
            states.push({ code: iso, name, countryCode, uniqueKey: `state-${countryCode}-${iso}-${i}` });
        });
    }
    return states.sort((a, b) => a.name.localeCompare(b.name));
};
export const findCountryByName = (countryName, language = 'en') => {
    const countries = getCountries(language);
    const needle = countryName.toLowerCase().trim();
    return countries.find(c => c.name.toLowerCase() === needle)
        || countries.find(c => c.name.toLowerCase().includes(needle) || needle.includes(c.name.toLowerCase()))
        || null;
};
export const findRegionByCode = (regionCode, language = 'en') => {
    const [countryCode, stateCode] = regionCode.split('-');
    if (!countryCode || !stateCode)
        return null;
    return getStatesForCountry(countryCode, language).find(s => s.code === stateCode) ?? null;
};
export const isValidCountryCode = (countryCode, language = 'en') => {
    return countryCode in getCountriesCached(language);
};
export const isValidStateCode = (countryCode, stateCode, language = 'en') => {
    return getStatesForCountry(countryCode, language).some(s => s.code === stateCode);
};
export const getCountryWithRegions = (countryCode, language = 'en') => {
    const raw = getCountriesCached(language);
    const c = raw[countryCode];
    if (!c)
        return null;
    return {
        country: { code: countryCode, name: c.n, iso3: c.i3, numeric: c.nu, uniqueKey: `country-${countryCode}` },
        regions: getStatesForCountry(countryCode, language),
    };
};
// ─── Language management (backward-compat) ───────────────────────────────────
export function registerLanguage(_language, _database) {
    // No-op: CDN mode doesn't need manual registration
}
export function isLanguageRegistered(language) {
    return countriesCache.has(language);
}
export function getRegisteredLanguages() {
    return [...countriesCache.keys()];
}
export async function importLanguage(language) {
    await ensureGeoDataLoaded(language);
}
// ─── Prefetch ────────────────────────────────────────────────────────────────
// Kick off English fetch the moment this module is imported so the country
// dropdown is populated by the time the React tree renders.
if (typeof globalThis !== 'undefined' && typeof fetch !== 'undefined') {
    ensureGeoDataLoaded('en').catch(() => { });
}
